/**
 * Summary:
 * Central Vue Router configuration for the web app: public routes, role-scoped layouts,
 * lazy-loaded admin/booker sections, and navigation guards for auth and role checks.
 *
 * Responsibilities / Role:
 * - Declares all application routes (public landing, login, model, admin, booker, prospect).
 * - Runs `beforeEach` guards: load session when needed, block unauthenticated access,
 *   enforce role metadata (`MODEL`, `BOOKER`, `AGENCY_ADMIN`, `PROSPECT`), and allow admins
 *   into booker routes where configured.
 * - Wires shared child views (messages, alerts, profile, report-bug) under each role tree.
 *
 * Out of Scope:
 * - Authentication/session persistence (`UseAuth`, `Auth.ts`, Supabase client setup).
 * - Page business logic and data fetching (owned by individual view components).
 * - Mobile routing (`src/mobile/router/index.ts`).
 *
 * Dependencies:
 * - `vue-router` (`createRouter`, `createWebHistory`).
 * - `@/composables/UseAuth` for `currentSession`, `currentUser`, `authLoaded`, `loadSessionFromSupabase`.
 * - View components under `@/views/**` (static and lazy `import()` targets).
 */
import { createRouter, createWebHistory } from 'vue-router'
import { useAuth } from '@/composables/UseAuth'

// Load each screen on navigation so the public showcase does not download
// authenticated calendars, forms and role layouts. Routes and guards stay shared.
const WelcomePage = () => import('@/views/WelcomePage.vue')
const Login = () => import('@/views/Login.vue')
const M_LoggedIn = () => import('@/views/model/M_LoggedIn.vue')
const CreateAgency = () => import('@/views/CreateAgency.vue')
const AcceptInvitation = () => import('@/views/AcceptInvitation.vue')
const Model_Info = () => import('@/views/components/Model_Info.vue')
const ModelPlanning = () => import('@/views/model/ModelPlanning.vue')
const ModelMyInfos = () => import('@/views/model/ModelMyInfos.vue')
const ModelHome = () => import('@/views/model/ModelHome.vue')
const ModelMyAgency = () => import('@/views/model/ModelMyAgency.vue')
const Profile = () => import('@/views/shared/Profile.vue')

// Lazy-loaded conversation component (used by all roles)
const Messages = () => import('@/views/shared/Messages.vue')
const AgencyAlerts = () => import('@/views/shared/AgencyAlerts.vue')
const ReportBug = () => import('@/views/shared/ReportBug.vue')
const ProspectFormLayout = () => import('@/views/prospect-form/ProspectFormLayout.vue')
const ProspectFormStep1 = () => import('@/views/prospect-form/ProspectFormStep1.vue')
const ProspectFormStep2 = () => import('@/views/prospect-form/ProspectFormStep2.vue')
const ProspectFormStep3 = () => import('@/views/prospect-form/ProspectFormStep3.vue')
const ProspectFormComplete = () => import('@/views/prospect-form/ProspectFormComplete.vue')
const Pr_LoggedIn = () => import('@/views/prospect/Pr_LoggedIn.vue')
const ProspectDashboard = () => import('@/views/prospect/ProspectDashboard.vue')

// Lazy-loaded admin-only routes
const A_LoggedIn = () => import('@/views/admin/A_LoggedIn.vue')
const AdminDashboard = () => import('@/views/admin/AdminDashboard.vue')
const AgencySettings = () => import('@/views/admin/AgencySettings.vue')
const UserManagement = () => import('@/views/admin/UserManagement.vue')
const InvitationManagement = () => import('@/views/admin/InvitationManagement.vue')
const UserLogs = () => import('@/views/admin/UserLogs.vue')

// Lazy-loaded booker (and also admin) routes
const B_LoggedIn = () => import('@/views/booker/B_LoggedIn.vue')
const Agenda = () => import('@/views/booker/Agenda.vue')
const Model_infos = () => import('@/views/booker/Model_infos.vue')
const Models = () => import('@/views/booker/Models.vue')
const ClientManagement = () => import('@/views/booker/ClientManagement.vue')

// routes de l'application
const routes = [
  { path: '/', component: WelcomePage },
  {
    path: '/model',
    component: M_LoggedIn,
    meta: { requiresAuth: true, role: 'MODEL' },
    children: [
      { path: '', redirect: '/model/home' },
      { path: 'home', component: ModelHome },
      { path: 'planning', component: ModelPlanning },
      { path: 'my-agency', component: ModelMyAgency },
      { path: 'my-infos', redirect: '/model/profile' },
      { path: 'alerts', component: AgencyAlerts },
      { path: 'messages', component: Messages },
      { path: 'messages/:channelId', component: Messages, props: true },
      { path: 'profile', component: ModelMyInfos },
      { path: 'report-bug', component: ReportBug },
  ],
  },
  {
    path: '/admin',
    component: A_LoggedIn,
    meta: { requiresAuth: true, role: 'AGENCY_ADMIN' },
    children: [
      { path: '', redirect: '/admin/dashboard' },
      { path: 'dashboard', component: AdminDashboard },
      { path: 'settings', component: AgencySettings },
      { path: 'users', component: UserManagement },
      { path: 'invitations', component: InvitationManagement },
      { path: 'logs', component: UserLogs },
      { path: 'alerts', component: AgencyAlerts },
      { path: 'messages', component: Messages },
      { path: 'messages/:channelId', component: Messages, props: true },
      { path: 'profile', component: Profile },
      { path: 'report-bug', component: ReportBug },
      // Réutilisation des vues booker
      { path: 'clients', component: ClientManagement },
      { path: 'createevent', redirect: '/admin/agenda' },
      {
        path: 'agenda',
        component: Agenda,
        children: [{ path: ':modelId', name: 'AdminModelInfos', component: Model_infos, props: true }],
      },
      {
        path: 'models',
        component: Models,
        children: [{ path: ':modelId', name: 'AdminAgencyModelInfo', component: Model_Info, props: true }],
      },
    ],
  },
  {
    path: '/booker',
    component: B_LoggedIn,
    meta: { requiresAuth: true, role: 'BOOKER' },
    children: [
      { path: '', redirect: '/booker/dashboard' },
      { path: 'dashboard', component: AdminDashboard },
      { path: 'createevent', redirect: '/booker/agenda' },
      { path: 'alerts', component: AgencyAlerts },
      { path: 'messages', component: Messages },
      { path: 'messages/:channelId', component: Messages, props: true },
      { path: 'profile', component: Profile },
      { path: 'report-bug', component: ReportBug },
      {
        path: 'agenda',
        component: Agenda,
        children: [{ path: ':modelId', name: 'ModelInfos', component: Model_infos, props: true }],
      },
      {
        path: 'models',
        component: Models,
        children: [{ path: ':modelId', name: 'ModelInfo', component: Model_Info, props: true }],
      },
      { path: 'clients', component: ClientManagement },
    ],
  },
  {
    path: '/prospect',
    component: Pr_LoggedIn,
    meta: { requiresAuth: true, role: 'PROSPECT' },
    children: [
      { path: '', redirect: '/prospect/dashboard' },
      { path: 'dashboard', component: ProspectDashboard },
      { path: 'profile', component: Profile },
    ],
  },
  {
    path: '/prospect-form',
    component: ProspectFormLayout,
    children: [
      { path: '', redirect: '/prospect-form/step-1' },
      { path: 'step-1', component: ProspectFormStep1 },
      { path: 'step-2', component: ProspectFormStep2 },
      { path: 'step-3', component: ProspectFormStep3 },
      { path: 'complete', component: ProspectFormComplete },
    ],
  },
  { path: '/Login', component: Login },
  { path: '/create-agency', component: CreateAgency },
  { path: '/accept-invite', alias: '/accept-invitation', component: AcceptInvitation },
]

// création du routeur
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.beforeEach(async (to, from, next) => {
  const { currentSession, currentUser, authLoaded, loadSessionFromSupabase } = useAuth()

  // Si le chargement de l'authentification n'est pas encore fait, on le fait avant de continuer
  if (to.meta.requiresAuth && !authLoaded.value) {
    await loadSessionFromSupabase()
  }

  // Vérifie si la route nécessite une authentification (interdit l'accès aux utilisateurs non connectés)
  if (to.meta.requiresAuth && !currentSession.value) {
    return next({ path: '/Login', query: { redirect: to.fullPath } })
  }
  if (to.meta.requiresAuth && !currentUser.value) {
    return next({ path: '/Login', query: { redirect: to.fullPath } })
  }
  // Vérifie si la route nécessite un rôle spécifique (interdit l'accès aux utilisateurs sans le rôle requis)
  if (to.meta.role) {
    if (!currentUser.value) {
      return next('/Login')
    }

    const requiredRole = to.meta.role
    const userRole = currentUser.value.role

    // Les admins peuvent accéder aux routes booker
    if (requiredRole === 'BOOKER' && userRole !== 'BOOKER' && userRole !== 'AGENCY_ADMIN') {
      return next('/Login')
    }
    // Pour les autres rôles (MODEL, PROSPECT, AGENCY_ADMIN), vérification stricte
    else if (requiredRole !== 'BOOKER' && userRole !== requiredRole) {
      return next('/Login')
    }
  }

  next()
})

export default router
